import React from 'react';
import { statusStyle, statusLabel } from '../utils/constants';
import { percent } from '../utils/format';

/**
 * Piccoli mattoncini UI condivisi dalla feature batch. Puramente presentazionali,
 * tema chiaro/scuro via classi Tailwind `dark:`, focus visibile e semantica
 * accessibile dove serve.
 */

export function Card({ children, className = '', as: Tag = 'section', ...rest }) {
  return (
    <Tag
      className={`rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function SectionTitle({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary/20">
            <Icon size={18} />
          </span>
        )}
        <div>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

export function Badge({ children, className = '', title }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

export function StatusBadge({ status }) {
  return <Badge className={statusStyle(status)}>{statusLabel(status)}</Badge>;
}

export function ProgressBar({ done, total, colorClass = 'bg-primary', className = '' }) {
  const pct = percent(done, total);
  return (
    <div
      className={`h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700 ${className}`}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 py-10 text-center dark:border-gray-600">
      {Icon && <Icon className="text-gray-400 dark:text-gray-500" size={28} />}
      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</p>
      {hint && <p className="max-w-sm text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
}

export function Spinner({ className = 'h-5 w-5' }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-primary border-t-transparent ${className}`}
      aria-hidden="true"
    />
  );
}

/** Pulsante primario coerente con il tema. */
export function Button({
  children,
  variant = 'primary',
  className = '',
  icon: Icon,
  ...rest
}) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50';
  const variants = {
    primary: 'bg-primary text-white shadow-sm hover:bg-primary/90',
    secondary:
      'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600',
    ghost:
      'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700',
    danger:
      'bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600',
  };
  return (
    <button
      type="button"
      className={`${base} ${variants[variant] || variants.primary} ${className}`}
      {...rest}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

/** Etichetta di conteggio compatta (es. "3 selezionate"). */
export function CountPill({ children }) {
  return (
    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary dark:bg-primary/20">
      {children}
    </span>
  );
}
